"use client";

import { useState } from "react";
import { useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";

type Mode = "encode" | "decode";

const ALPHABET_SIZE = 26;
const UPPER_A = 65;
const LOWER_A = 97;

type Strings = { [K in keyof typeof CAESAR_CIPHER_STRINGS]: string };

const MODES: { value: Mode; label: keyof Strings }[] = [
  { value: "encode", label: "modeEncode" },
  { value: "decode", label: "modeDecode" },
];

const ALL_SHIFTS = Array.from({ length: 25 }, (_, i) => i + 1);

function shiftChar(char: string, shift: number): string {
  const code = char.charCodeAt(0);
  if (code >= UPPER_A && code < UPPER_A + ALPHABET_SIZE) {
    return String.fromCharCode(
      UPPER_A + ((code - UPPER_A + shift) % ALPHABET_SIZE)
    );
  }
  if (code >= LOWER_A && code < LOWER_A + ALPHABET_SIZE) {
    return String.fromCharCode(
      LOWER_A + ((code - LOWER_A + shift) % ALPHABET_SIZE)
    );
  }
  return char;
}

function caesarShift(text: string, shift: number): string {
  const normalized = ((shift % ALPHABET_SIZE) + ALPHABET_SIZE) % ALPHABET_SIZE;
  let result = "";
  for (const char of text) {
    result += shiftChar(char, normalized);
  }
  return result;
}

function clampShift(value: number): number {
  if (Number.isNaN(value)) {
    return 1;
  }
  return Math.min(25, Math.max(1, Math.round(value)));
}

/** English strings. A translated content file overrides these through `ui`. */
export const CAESAR_CIPHER_STRINGS = {
  mode: "Mode",
  modeEncode: "Encode",
  modeDecode: "Decode",
  shift: "Shift: ",
  shiftValue: "Shift value",
  rot13Preset: "ROT13 preset",
  bruteForceModeShowAll: "Brute-force mode — show all 25 shifts (decoding aid)",
  plainText: "Plain text",
  cipherText: "Cipher text",
  attackAtDawn: "Attack at dawn",
  decodedText: "Decoded text",
  all25ShiftsAppliedAs: "All 25 shifts (applied as decode)",
  shift2: "Shift ",
  copied: "Copied!",
  copyResult: "Copy Result",
  clear: "Clear",
  howTheCaesarCipherWorks: "How the Caesar Cipher Works",
  theCaesarCipherIsOne:
    "The Caesar cipher is one of the oldest known encryption techniques, named after Julius Caesar, who used it around 58 BC to protect military correspondence. Each letter is replaced by the letter a fixed number of positions further along the alphabet, wrapping around from Z back to A. With a shift of 3, HELLO becomes KHOOR.",
  decodingSimplyShiftsInThe:
    "Decoding simply shifts in the opposite direction. Because there are only 25 meaningful shifts, the cipher offers no real security today — but that same weakness makes it a perfect introduction to cryptography, and the brute-force mode above shows exactly how an attacker would break it.",
} as const;

export default function CaesarCipherWidget() {
  const [input, setInput] = useState("");
  const [mode, setMode] = useState<Mode>("encode");
  const [shift, setShift] = useState(3);
  const [bruteForce, setBruteForce] = useState(false);
  const [copied, setCopied] = useState(false);

  const effectiveShift = mode === "encode" ? shift : ALPHABET_SIZE - shift;
  const output = input.length === 0 ? "" : caesarShift(input, effectiveShift);

  function handleCopy() {
    if (output.length === 0) {
      return;
    }
    writeToClipboard(output).then((copied) => {
      if (!copied) {
        return;
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function handleClear() {
    setInput("");
    setCopied(false);
  }

  const strings = useToolUi(CAESAR_CIPHER_STRINGS);

  return (
    <>
      {/* Mode */}
      <section className="space-y-3">
        <p className="font-medium text-foreground text-sm">{strings.mode}</p>
        <div className="flex flex-wrap gap-3">
          {MODES.map((m) => (
            <label
              className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5"
              key={m.value}
            >
              <input
                checked={mode === m.value}
                className="accent-primary"
                name="cipher-mode"
                onChange={() => setMode(m.value)}
                type="radio"
                value={m.value}
              />
              {strings[m.label]}
            </label>
          ))}
        </div>
      </section>
      {/* Shift */}
      <section className="space-y-3">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="shift-slider"
        >
          {strings.shift}
          {shift}
        </label>
        <div className="flex flex-wrap items-center gap-4">
          <input
            className="w-full max-w-sm accent-primary"
            id="shift-slider"
            max={25}
            min={1}
            onChange={(e) => setShift(clampShift(Number(e.target.value)))}
            type="range"
            value={shift}
          />
          <input
            aria-label={strings.shiftValue}
            className="w-20 rounded-md border border-border bg-card px-2 py-1 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            max={25}
            min={1}
            onChange={(e) => setShift(clampShift(Number(e.target.value)))}
            type="number"
            value={shift}
          />
          <button
            className="rounded-lg border border-border bg-card px-4 py-1.5 font-medium text-foreground text-sm transition-colors hover:bg-muted"
            onClick={() => setShift(13)}
            type="button"
          >
            {strings.rot13Preset}
          </button>
        </div>
        <label className="flex cursor-pointer items-center gap-2 text-foreground text-sm">
          <input
            checked={bruteForce}
            className="accent-primary"
            onChange={(e) => setBruteForce(e.target.checked)}
            type="checkbox"
          />
          {strings.bruteForceModeShowAll}
        </label>
      </section>
      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="cipher-input"
        >
          {mode === "encode" ? strings.plainText : strings.cipherText}
        </label>
        <textarea
          className="min-h-[140px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="cipher-input"
          onChange={(e) => {
            setInput(e.target.value);
            setCopied(false);
          }}
          placeholder={strings.attackAtDawn}
          value={input}
        />
      </div>
      {/* Output */}
      {output.length > 0 && !bruteForce && (
        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="cipher-output"
          >
            {mode === "encode" ? strings.cipherText : strings.decodedText}
          </label>
          <textarea
            className="min-h-[140px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-primary"
            id="cipher-output"
            readOnly
            value={output}
          />
        </div>
      )}
      {/* Brute force list */}
      {input.length > 0 && bruteForce && (
        <section className="space-y-2">
          <p className="font-medium text-foreground text-sm">
            {strings.all25ShiftsAppliedAs}
          </p>
          <ul className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-card">
            {ALL_SHIFTS.map((s) => (
              <li className="flex items-baseline gap-3 px-4 py-2" key={s}>
                <span className="w-16 shrink-0 font-medium text-muted-foreground text-xs">
                  {strings.shift2}
                  {s}
                </span>
                <span className="break-all font-mono text-foreground text-sm">
                  {caesarShift(input, ALPHABET_SIZE - s)}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90 disabled:opacity-40"
          disabled={output.length === 0}
          onClick={handleCopy}
          type="button"
        >
          {copied ? strings.copied : strings.copyResult}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={input.length === 0}
          onClick={handleClear}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      {/* How it works */}
      <section className="space-y-3 rounded-lg border border-border bg-card p-6">
        <h2 className="font-semibold text-foreground text-xl">
          {strings.howTheCaesarCipherWorks}
        </h2>
        <p className="text-muted-foreground text-sm leading-relaxed">
          {strings.theCaesarCipherIsOne}
        </p>
        <p className="text-muted-foreground text-sm leading-relaxed">
          {strings.decodingSimplyShiftsInThe}
        </p>
      </section>
    </>
  );
}
